// Where the payment provider sends the player back after paying (`{PLAYER_WEB_URL}/payment-complete`). Public, because
// the provider's redirect carries nothing the page can rely on: what the payment was for was remembered in this tab
// before the player left (`lib/payments.ts`). Signed in with a payment remembered, the page asks the api how it stands
// every two seconds, for up to three minutes, and says so in words; nothing is credited here, only reported.
// Without a remembered payment (the player paid on their phone from a QR at the PC or the counter), it just thanks them.

import { useTranslation } from '@clavian/i18n/react';
import { Spinner } from '@clavian/ui-web';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';

import { PAYMENT_GIVE_UP_MS, queryKeys, useMyPayment, useSession } from '../api/queries';
import { AuthLayout } from '../auth/AuthLayout';
import { formatterFor } from '../lib/format';
import { clearPendingPayment, readPendingPayment, type PendingPayment } from '../lib/payments';

export function PaymentCompletePage(): ReactNode {
  const { t } = useTranslation('player');
  const session = useSession();
  // Read once, on arrival.
  const [pending] = useState<PendingPayment | null>(readPendingPayment);

  if (pending === null) return <Thanks signIn={false} />;
  if (session.isPending) {
    return (
      <AuthLayout title={t('paymentComplete.pendingTitle')} homeLink={false}>
        <Spinner size="lg" labelHidden label={t('paymentComplete.pendingTitle')} />
      </AuthLayout>
    );
  }
  if (session.data == null) return <Thanks signIn />;
  return <Waiting pending={pending} />;
}

function Thanks({ signIn }: { readonly signIn: boolean }): ReactNode {
  const { t } = useTranslation('player');
  return (
    <AuthLayout title={t('paymentComplete.thanksTitle')} homeLink={false}>
      <div className="pw-form">
        <p className="pw-handoff__body" role="status">
          {t('paymentComplete.thanks')}
        </p>
        {signIn ? (
          <>
            <p className="pw-handoff__body">{t('paymentComplete.thanksSignIn')}</p>
            <Link to="/sign-in" search={{ redirect: '/payment-complete' }} className="pw-btn pw-btn--primary pw-btn--lg">
              {t('paymentComplete.signIn')}
            </Link>
          </>
        ) : null}
      </div>
    </AuthLayout>
  );
}

function Waiting({ pending }: { readonly pending: PendingPayment }): ReactNode {
  const { t } = useTranslation('player');
  const queryClient = useQueryClient();
  const [gaveUp, setGaveUp] = useState(false);
  const payment = useMyPayment(pending.id, !gaveUp);
  const status = payment.data?.status;
  const settled = status !== undefined && status !== 'pending';

  useEffect(() => {
    if (settled) return undefined;
    const id = window.setTimeout(() => setGaveUp(true), PAYMENT_GIVE_UP_MS);
    return () => window.clearTimeout(id);
  }, [settled]);

  useEffect(() => {
    if (!settled) return;
    clearPendingPayment();
    if (status === 'succeeded') {
      void queryClient.invalidateQueries({ queryKey: queryKeys.wallets });
      void queryClient.invalidateQueries({ queryKey: queryKeys.passes });
      void queryClient.invalidateQueries({ queryKey: queryKeys.orders });
      void queryClient.invalidateQueries({ queryKey: queryKeys.live });
      if (payment.data !== undefined) void queryClient.invalidateQueries({ queryKey: queryKeys.myVenueShop(payment.data.venue.id) });
    }
  }, [settled, status, queryClient, payment.data]);

  if (status === 'succeeded' && payment.data !== undefined) {
    const data = payment.data;
    if (data.purpose === 'wallet_topup') {
      return (
        <AuthLayout title={t('paymentComplete.topUpTitle')} homeLink={false}>
          <div className="pw-form">
            <p className="pw-handoff__body" role="status">
              {t('paymentComplete.topUpDone', { amount: formatterFor(data.amount.currency).money(data.amount), organization: data.organization.name })}
            </p>
            <p className="pw-handoff__body pw-muted">{t('paymentComplete.topUpExtra')}</p>
            <Link to="/wallet" className="pw-btn pw-btn--primary pw-btn--lg">
              {t('paymentComplete.seeWallet')}
            </Link>
          </div>
        </AuthLayout>
      );
    }
    return (
      <AuthLayout title={t('paymentComplete.passTitle')} homeLink={false}>
        <div className="pw-form">
          <p className="pw-handoff__body" role="status">
            {t('paymentComplete.passDone', { name: pending.name ?? '' })}
          </p>
          <Link to="/passes" className="pw-btn pw-btn--primary pw-btn--lg">
            {t('paymentComplete.seePasses')}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  if (settled) {
    const slug = pending.venueSlug ?? payment.data?.venue.slug ?? null;
    return (
      <AuthLayout title={t('paymentComplete.failedTitle')} homeLink={false}>
        <div className="pw-form">
          <p className="pw-handoff__body" role="status">
            {t('paymentComplete.failed')}
          </p>
          {slug !== null ? (
            <Link to="/venues/$slug" params={{ slug }} className="pw-btn pw-btn--primary pw-btn--lg">
              {t('paymentComplete.backToVenue')}
            </Link>
          ) : (
            <Link to="/venues" className="pw-btn pw-btn--primary pw-btn--lg">
              {t('paymentComplete.backToVenue')}
            </Link>
          )}
        </div>
      </AuthLayout>
    );
  }

  if (gaveUp) {
    return (
      <AuthLayout title={t('paymentComplete.slowTitle')} homeLink={false}>
        <div className="pw-form">
          <p className="pw-handoff__body" role="status">
            {t('paymentComplete.slow')}
          </p>
          <Link to="/home" className="pw-btn pw-btn--secondary pw-btn--lg">
            {t('paymentComplete.home')}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  // Asking again; if the api cannot say (not the player's payment, or none), say so rather than spin for ever.
  if (payment.isError && payment.fetchStatus === 'idle') {
    return (
      <AuthLayout title={t('paymentComplete.unreadTitle')} homeLink={false}>
        <div className="pw-form">
          <p className="pw-handoff__body" role="status">
            {t('paymentComplete.unread')}
          </p>
          <Link to="/home" className="pw-btn pw-btn--secondary pw-btn--lg">
            {t('paymentComplete.home')}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('paymentComplete.pendingTitle')} homeLink={false}>
      <div className="pw-form">
        <Spinner size="lg" labelHidden label={t('paymentComplete.pendingTitle')} />
        <p className="pw-handoff__body" role="status">
          {t('paymentComplete.pendingBody')}
        </p>
      </div>
    </AuthLayout>
  );
}
